import { LoanSetupScreen } from "@/screens/loans/components/setup/LoanSetupScreen"
import { Accordion } from "@/components/reusables/ui/accordion"
import { ScrollView } from "react-native"
import { useEffect, useRef, useState } from "react"
import { PropertyQuestions } from "@/screens/loans/components/setup/PropertyQuestions"
import { ContractStatusQuestions } from "@/screens/loans/components/setup/ContractStatusQuestion"
import {
  BorrowerAndContactQuestion,
  type BorrowerContactRef,
} from "@/screens/loans/components/setup/BorrowerAndContactQuestion"
import { DateQuestion } from "@/screens/loans/components/setup/DateQuestion"
import { addDays } from "date-fns"
import { PropertyDetails } from "@/screens/loans/components/setup/PropertyDetails"
import {
  type LoanApplication,
  type PlaceData,
  useLoanContext,
  useNewLoanContext,
} from "@/screens/loans/components/LoanContext"
import { NegativeROISheet } from "@/screens/loans/components/NegativeROISheet"
import { setupScreenAccordionOpened } from "@/analytics/events/loans"

const DEFAULT_CLOSING = addDays(new Date(), 14)

function checkIsComplete(loanApplication?: LoanApplication) {
  if (!loanApplication?.id || !loanApplication.contractStatus) {
    return false
  }

  if (
    loanApplication.contractStatus === "refinance" &&
    !loanApplication.originalPurchaseDate &&
    !loanApplication.targetCloseDate
  ) {
    return false
  }

  if (
    loanApplication.contractStatus !== "unknown" &&
    !loanApplication.targetCloseDate
  ) {
    return false
  }

  if (
    !loanApplication.afterRepairValue ||
    loanApplication.afterRepairValue <= 0 ||
    !loanApplication.purchasePrice ||
    loanApplication.purchasePrice <= 0
  ) {
    return false
  }

  return true
}

function calculateNextScreen(
  loanApplication: LoanApplication | undefined,
  place: PlaceData | undefined,
  borrowerValid: boolean
) {
  if (!place && !loanApplication?.property) {
    return "property"
  } else if (!borrowerValid) {
    return "borrower"
  } else if (!loanApplication?.contractStatus) {
    return "contract-status"
  } else if (
    loanApplication?.contractStatus === "refinance" &&
    !loanApplication?.originalPurchaseDate
  ) {
    return "purchase-date"
  } else if (!loanApplication?.targetCloseDate) {
    return "closing-date"
  } else {
    return "property-details"
  }
}

function ensureDate(date: Date | string | null | undefined): Date | undefined {
  if (typeof date === "string") {
    return new Date(date)
  }

  return date || undefined
}

export const LoanPropertyScreen = ({
  onSubmit,
  backButton,
}: {
  onSubmit: () => Promise<void>
  backButton?: () => void
}) => {
  const { loanApplication, update } = useLoanContext()
  const { place } = useNewLoanContext()

  const [showNegativeROISheet, setShowNegativeROISheet] = useState(false)
  const [borrowerValid, setBorrowerValid] = useState(false)
  const borrowerRef = useRef<BorrowerContactRef>(null)

  const hasProperty = !!place || loanApplication?.id
  const isComplete = checkIsComplete(loanApplication)
  const [active, setActive] = useState<string | undefined>(
    calculateNextScreen(loanApplication, place, borrowerValid)
  )

  function nextScreen() {
    setActive(
      calculateNextScreen(loanApplication, place, borrowerValid)
    )
  }

  useEffect(() => {
    if (active) {
      setupScreenAccordionOpened({
        currentScreen: "Loan Setup - Property Info",
        accordionKey: active,
      })
    }
  }, [active])

  useEffect(() => {
    nextScreen()
  }, [
    loanApplication?.contractStatus,
    loanApplication?.targetCloseDate,
    loanApplication?.originalPurchaseDate,
    place,
  ])

  async function doSubmit() {
    borrowerRef.current?.flushContactFields()
    const costBasis =
      (loanApplication.purchasePrice || 0) +
      (loanApplication.rehabEstimate || 0) +
      (loanApplication.loanProduct === "refinance"
        ? loanApplication.constructionCostsSpent || 0
        : 0)
    const grossProfit = (loanApplication.afterRepairValue || 0) - costBasis
    if (grossProfit < 0) {
      setShowNegativeROISheet(true)
      return
    }
    await onSubmit()
  }

  return (
    <LoanSetupScreen
      title={"Property Info"}
      subTitle={"Tell us about your next deal"}
      stepData={[2, 4]}
      nextButtonProps={{
        title: "Next: Loan Products",
        disabled: !isComplete || !borrowerValid,
      }}
      onSubmit={doSubmit}
      backButton={backButton}
    >
      <ScrollView className="flex-1 px-5">
        <Accordion
          type="single"
          collapsible
          defaultValue={active}
          value={active}
          className="w-full pt-0 pb-5"
          onValueChange={(value) => {
            setActive(value)
          }}
        >
          <PropertyQuestions />

          {hasProperty && (
            <BorrowerAndContactQuestion
              ref={borrowerRef}
              onValidationChange={setBorrowerValid}
            />
          )}

          {hasProperty && (
            <ContractStatusQuestions
              contractStatus={loanApplication?.contractStatus || undefined}
              onChange={(contractStatus) => {
                update({ contractStatus })
              }}
            />
          )}

          {loanApplication?.contractStatus &&
            loanApplication?.contractStatus !== "refinance" &&
            loanApplication?.contractStatus !== "unknown" && (
              <DateQuestion
                header={"Closing Date"}
                text={"What is your target closing date?"}
                date={ensureDate(loanApplication?.targetCloseDate)}
                accordianKey={"closing-date"}
                defaultValue={DEFAULT_CLOSING}
                onChange={(date) => {
                  update({ targetCloseDate: date })
                }}
              />
            )}
          {loanApplication?.contractStatus &&
            loanApplication?.contractStatus === "refinance" && (
              <DateQuestion
                header={"Original Purchase Date"}
                text={"What was the purchase date of your current property?"}
                date={ensureDate(loanApplication?.originalPurchaseDate)}
                accordianKey={"purchase-date"}
                defaultValue={DEFAULT_CLOSING}
                onChange={(date) => {
                  update({ originalPurchaseDate: date?.toISOString() })
                }}
              />
            )}
          {loanApplication?.contractStatus &&
            loanApplication?.contractStatus === "refinance" && (
              <DateQuestion
                header={"Refinance Closing Date"}
                text={"What is your target closing date?"}
                date={ensureDate(loanApplication?.targetCloseDate)}
                accordianKey={"closing-date"}
                defaultValue={DEFAULT_CLOSING}
                onChange={(date) => {
                  update({ targetCloseDate: date })
                }}
              />
            )}

          {hasProperty && <PropertyDetails />}
        </Accordion>
      </ScrollView>
      <NegativeROISheet
        visible={showNegativeROISheet}
        onClose={(moveOn) => {
          setShowNegativeROISheet(false)
          if (moveOn) {
            void (async () => {
              await onSubmit()
            })()
          }
        }}
      />
    </LoanSetupScreen>
  )
}
